import { useApolloClient } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Delete } from "@mui/icons-material";
import {
  MenuItem,
  Button,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  ReactElement,
  useMemo,
  useState,
  SetStateAction,
  Dispatch,
} from "react";
import {
  Control,
  FieldValues,
  useFieldArray,
  UseFormSetValue,
  useForm,
  UseFormGetValues,
  UseFormWatch,
  useController,
} from "react-hook-form";
import { z } from "zod";

import { STATE_NAMES } from "@stw/states";
import {
  MetadataFieldTypes,
  MetadataStateFieldUISchema,
} from "@stw/yup-validators";

import {
  MetadataStateFieldFragment,
  UpdateMetadataStateFieldMutation,
  UpdateMetadataStateFieldMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { FieldForm } from "~frontend/components/campaignMetadata/FieldForm";
import { handlePostSubmit } from "~frontend/components/campaignMetadata/common";
import Select from "~frontend/components/forms/FormSelect";
import {
  RhfSelect,
  RhfSwitchField,
} from "~frontend/components/forms/react-hook-form-components";
import { RhfMultiSelectCheckbox } from "~frontend/components/forms/react-hook-form-components/RhfMultiSelectCheckbox";
import {
  UPDATE_METADATA_STATE_FIELD,
  GET_TENANT_METADATA_FIELDS,
} from "~frontend/graphql/campaignMetadata";
import useMutation from "~frontend/util/useMutation";

type StateFieldFormProps = {
  editField?: MetadataStateFieldFragment;
  initialName: string;
  onTypeChange: (name: string, type: MetadataFieldTypes) => void;
  onCancelClick: () => void;
};

type FormInput = FieldValues & z.input<typeof MetadataStateFieldUISchema>;
type FormOutput = FieldValues & z.infer<typeof MetadataStateFieldUISchema>;
type DefaultValueSelect = "Yes" | "No";

const StateFieldForm = FieldForm<FormInput>;

export function EditStateFieldForm({
  editField,
  onTypeChange,
  onCancelClick,
  initialName,
}: StateFieldFormProps): ReactElement {
  const id = editField ? editField.id : undefined;
  const [showEditSection, setShowEditSection] = useState(false);
  const initialValues: FormInput = useMemo(() => {
    if (!editField) {
      const statesValues = STATE_NAMES.map(s => ({ value: s }));
      return {
        name: initialName,
        required: false,
        isSingleSelect: true,
        defaultListValue: [],
        defaultValue: "",
        values: statesValues,
      };
    }
    const values = editField.configuration.values.map(v => ({ value: v }));
    const {
      id,
      name,
      required,
      defaultStateValue,
      configuration: { isSingleSelect },
    } = editField;
    let defaultValue = "";
    let defaultListValue: string[] = [];
    if (isSingleSelect && defaultStateValue.length > 0) {
      defaultValue = defaultStateValue[0];
    } else if (!isSingleSelect) {
      defaultListValue = defaultStateValue;
    }
    return {
      id,
      name,
      required,
      isSingleSelect,
      defaultListValue,
      defaultValue,
      values,
    };
  }, [editField, initialName]);
  const client = useApolloClient();
  const {
    control,
    handleSubmit,
    reset,
    setError,
    getValues,
    setValue,
    watch,
    formState: { isSubmitting },
  } = useForm({
    resolver: zodResolver(MetadataStateFieldUISchema),
    values: initialValues,
  });
  const { fields, append, remove, update } = useFieldArray({
    control: control,
    name: "values",
  });

  const [updateField] = useMutation<
    UpdateMetadataStateFieldMutation,
    UpdateMetadataStateFieldMutationVariables
  >(UPDATE_METADATA_STATE_FIELD, {
    refetchQueries: [GET_TENANT_METADATA_FIELDS],
    awaitRefetchQueries: true,
  });

  async function onSubmit(v: FormOutput) {
    const { name, required, defaultValue, defaultListValue, isSingleSelect } =
      v;
    const apiValues = isSingleSelect ? [defaultValue] : defaultListValue;
    const variables = {
      input: {
        id,
        name,
        required,
        defaultValue: apiValues,
        values: v.values?.map(v => v.value) || [],
        isSingleSelect,
      },
    };
    const { errors, data } = await updateField({
      variables,
    });
    handlePostSubmit({
      onSuccess: onCancelClick,
      setError,
      apolloClient: client,
      isNewField: !editField,
      data: data?.createOrUpdateCampaignMetadataStateField,
      errors,
    });
  }

  function onFormCancelClick() {
    reset();
    onCancelClick();
  }
  function addEmptySelectOption() {
    append({ value: "" });
  }

  function removeSelectOption(index: number) {
    if (fields.length === 1) {
      setError("values.0.value", {
        message: "At least one option is required.",
      });
      return;
    }
    remove(index);
  }
  const stateIsSingleSelect = watch("isSingleSelect");
  const singleOrMultiText = stateIsSingleSelect ? "single" : "multi";
  const stateSwitchLabel = `State is a ${singleOrMultiText} select field`;
  const stateLookup = new Set(fields.map(s => s.value));

  return (
    <StateFieldForm
      editField={editField}
      onTypeChange={onTypeChange}
      currentFieldType={MetadataFieldTypes.STATE}
      onCancelClick={onFormCancelClick}
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit(onSubmit)}
      control={control}
      getValues={getValues}
      watch={watch}
    >
      {!showEditSection && (
        <FlexBox justifyContent="space-between" alignItems="center" gap={1}>
          <MinimalSelect
            control={control}
            watch={watch}
            setShowEdit={setShowEditSection}
          />
        </FlexBox>
      )}
      {showEditSection && (
        <>
          <Button
            color="info"
            variant="contained"
            onClick={() => setShowEditSection(!showEditSection)}
            sx={{ mb: 1 }}
          >
            Hide values
          </Button>
          {fields.map((f, index) => (
            <FlexBox key={f.id} direction="column">
              <FlexBox direction="row" gap={2} sx={{ pl: 2 }}>
                <DefaultValueSelect
                  fieldValue={f.value}
                  index={index}
                  setValue={setValue}
                  getValues={getValues}
                  watch={watch}
                />
                {f.value !== "" ? (
                  <TextField
                    label={`${stateIsSingleSelect ? "Single" : "Multi"} Select Option ${index + 1}`}
                    style={{ flexGrow: 1 }}
                    value={f.value}
                    disabled
                  />
                ) : (
                  <StateSelector
                    fieldValue={f.value}
                    onChange={(value: string) => update(index, { value })}
                    control={control}
                    index={index}
                    stateLookup={stateLookup}
                  />
                )}
                <FlexBox alignItems={"center"}>
                  <IconButton onClick={() => removeSelectOption(index)}>
                    <Delete />
                  </IconButton>
                </FlexBox>
              </FlexBox>
            </FlexBox>
          ))}

          {fields.length < STATE_NAMES.length && (
            <FlexBox direction="row" sx={{ pb: 1, pt: 1 }}>
              <Button
                variant="contained"
                color="info"
                sx={{ maxWidth: "40%" }}
                onClick={addEmptySelectOption}
              >
                + ADD SELECT OPTION
              </Button>
            </FlexBox>
          )}
        </>
      )}
      {editField ? (
        <FormControlLabel
          control={
            <Switch
              name="single select"
              disabled
              checked={stateIsSingleSelect}
            />
          }
          label={stateSwitchLabel}
        />
      ) : (
        <RhfSwitchField
          control={control}
          name="isSingleSelect"
          label={stateSwitchLabel}
        />
      )}
    </StateFieldForm>
  );
}

function DefaultValueSelect({
  fieldValue,
  getValues,
  setValue,
  watch,
}: {
  fieldValue: string;
  index: number;
  getValues: UseFormGetValues<FormInput>;
  setValue: UseFormSetValue<FormInput>;
  watch: UseFormWatch<FormInput>;
}) {
  const defaultValue = watch("defaultValue");
  const defaultListValue = watch("defaultListValue");
  const isSingleSelect = getValues("isSingleSelect");
  const isSetToDefault = isSingleSelect
    ? fieldValue !== "" && fieldValue === defaultValue
    : defaultListValue.includes(fieldValue);
  const value = isSetToDefault ? "Yes" : "No";

  function onDefaultValueChange(
    menuSelect: DefaultValueSelect,
    selectValue: string
  ) {
    if (menuSelect === "Yes") {
      setValue("defaultValue", selectValue);
    } else {
      if (selectValue === defaultValue) {
        setValue("defaultValue", "");
      }
    }
  }

  function onDefaultListValueChange(
    menuSelect: DefaultValueSelect,
    selectValue: string
  ) {
    let newDefaultValues: string[] = [];
    if (menuSelect === "Yes") {
      newDefaultValues = [...new Set([...defaultListValue, selectValue])];
    } else if (menuSelect === "No" && defaultListValue.includes(selectValue)) {
      newDefaultValues = defaultListValue.filter(v => v !== selectValue);
    }
    setValue("defaultListValue", newDefaultValues);
  }

  function onSelectChange(menuSelect: DefaultValueSelect) {
    if (isSingleSelect) {
      onDefaultValueChange(menuSelect, fieldValue);
    } else {
      onDefaultListValueChange(menuSelect, fieldValue);
    }
  }

  return (
    <Select
      label="Default Value?"
      value={value}
      onChange={e => onSelectChange(e.target.value as DefaultValueSelect)}
      sx={{ minWidth: "25%" }}
      disabled={!fieldValue}
    >
      <MenuItem value="No" key="No">
        No
      </MenuItem>
      <MenuItem value="Yes" key="Yes">
        Yes
      </MenuItem>
    </Select>
  );
}

function MinimalSelect({
  setShowEdit,
  watch,
  control,
}: {
  setShowEdit: Dispatch<SetStateAction<boolean>>;
  watch: UseFormWatch<FormInput>;
  control: Control<FormInput>;
}) {
  const isSingleSelect = watch("isSingleSelect");
  const { fields } = useFieldArray({
    control: control,
    name: "values",
  });

  const name = isSingleSelect ? "defaultValue" : "defaultListValue";
  const label = isSingleSelect ? "Default Value" : "Default Values";
  const key = isSingleSelect
    ? "defaultValueControl"
    : "defaultListValueControl";
  const options = fields.map(fv => ({ id: fv.value, label: fv.value }));
  const editButton = (
    <Button
      color="info"
      variant="contained"
      sx={{ mt: 1 }}
      size="large"
      onClick={() => setShowEdit(true)}
    >
      Edit Values
    </Button>
  );
  if (!isSingleSelect) {
    return (
      <>
        <RhfMultiSelectCheckbox
          key={key}
          control={control}
          name={name}
          label={label}
          fullWidth
          options={options}
          sx={{ mb: 2 }}
        />
        {editButton}
      </>
    );
  }

  return (
    <>
      <RhfSelect
        key={key}
        control={control}
        name={name}
        label={label}
        fullWidth
        multiple={!isSingleSelect}
        withNoneOption={!!isSingleSelect}
      >
        {fields.map(fv => (
          <MenuItem key={fv.id} value={fv.value}>
            {fv.value}
          </MenuItem>
        ))}
      </RhfSelect>
      {editButton}
    </>
  );
}

function StateSelector({
  fieldValue,
  stateLookup,
  index,
  onChange,
  control,
}: {
  fieldValue: string;
  index: number;
  stateLookup: Set<string>;
  onChange: (value: string) => void;
  control: Control<FormInput>;
}) {
  const name = `values.${index}.value`;
  const {
    fieldState: { error },
  } = useController({ control, name });

  // when there is an empty value, we want to show a select of all states
  // Once the user selects a state, the field switches to a display only.
  const statesAvailable = STATE_NAMES.filter(s => !stateLookup.has(s)).map(
    v => ({ value: v })
  );
  return (
    <Select
      label="Select a state"
      key={name}
      onChange={e => onChange(e.target.value)}
      value={fieldValue}
      error={!!error}
      helperText={error?.message}
      fullWidth
    >
      {statesAvailable.map(s => (
        <MenuItem key={s.value} value={s.value}>
          {s.value}
        </MenuItem>
      ))}
    </Select>
  );
}
